import type { MathParameter } from './Scenes/MathConditionUI';

export type ConditionOperator = 'eq' | 'lt' | 'gt';
export type TimerCondition = {
	dminslimit: string;
	rrtrig: { rr: 'dt:t' };
	_gui_type: 'timer';
};

type DateAndTime = {
	eq: [{ rr: 'dt:t' }, string] | [string, { rr: 'dt:t' }]; //the second one must be checked;
};

/** Once executed with date and time
 *  { "eq":[ { "rr":"dt:dt" }, "2021-06-11 00:00" ],
	"_gui_type":"ot_cond" } 
*/
export type IOneAtDateAndTimeCondition = DateAndTime & {
	_gui_type: 'ot_cond';
};

/** Daily condition - WHOLE WEEK (the same structure as Execute once at )
 *  { "eq":[{ "rr":"dt:t" }, "00:00"], "_gui_type":"d_cond"}
 */
export type WholeWeekAtTime = DateAndTime & {
	_gui_type: 'd_cond';
};

/**
Daily condition - Weekdays and Time selection
  { "and":[ { "eq":[ { "rr":"dt:t" }, "00:00"] },
			{ "wday":"now", "isin":"1 2 3 4 5 6 "}
		],
	"_gui_type":"d_cond"
} */
export type DailyConditions = {
	and: [{ eq: [{ rr: 'dt:t' }, string] }, { wday: 'now'; isin: string }];
	_gui_type: 'd_cond';
};

/**
{ indailyint: { dows: '1 2 3 4 5 6 7', startat: '00:00', dur: 1440 } }*/
export type SceneActiveTime = {
	indailyint: {
		dows: string;
		startat: string;
		dur: number;
	};
};

/**
 * Optional server-side transformation applied to a reference before it is compared.
 * It is generic on purpose: `method` selects the transformation implemented by the
 * Scene Service and `params` carries its arguments, so any device can add one
 * without changing the condition plumbing.
 *
 * Camera object recognition, where `minscore` is a 0-1 confidence ratio:
 * { "method": "cam_recognition", "params": { "type": "person", "nobjs": 2, "minscore": 0.55 } }
 */
export type SceneConditionTransform = {
	method: string;
	params: Record<string, string | number | boolean>;
};

export type ISceneTrigger = {
	rr: string;
	transform?: SceneConditionTransform;
};

/**
{"rr":"sun:<locid>:<rise/set><+/-><offset>"}
sunset condition {rr:"sun:<locid>:set+0" ...}
sunrise condition {rr:"sun:<locid>:rise-30" ...}
*/
export type SunriseSunsetTrigger = ISceneTrigger;

/**
 *  * On any change executed it
 *	{ eq: [ {"rr":"owm:<locid>+<hour offset>:<cond>"}, value ] }
	{ lt: [ {"rr":"owm:<locid>+<hour offset>:<cond>"}, value ] }
	{ gt: [ {"rr":"owm:<locid>+<hour offset>:<cond>"}, value ] }
*/
export type WeatherTriggerOnAnyChange = {
	eq?: [ISceneTrigger, number | string];
	lt?: [ISceneTrigger, number | string];
	gt?: [ISceneTrigger, number | string];
} & GUI_PURPOSE_COND;

/**
 * Once executed device trigger
 * {
 * rr: `owm:<locid>+<hour offset>:<cond>`,
 * thsop: operator,
 * thsval: desiredVal,
 * _gui_purpose: 'condition' //const value,
 * }
 */
export type WeatherOnceTrigger = ISceneTrigger & {
	thsop: ConditionOperator;
	thsval: string | number;
} & GUI_PURPOSE_COND;

export type DeviceTrigger = ISceneTrigger;

/**
 * Once executed device trigger
 * {
 * rr: `dev:decimalId:condByCh`,
 * thsop: operator,
 * thsval: desiredVal,
 * _gui_purpose: 'condition' //const value,
 * hold_off?: inMinOptional
 * }
 */
export type DeviceOnceTrigger = DeviceTrigger & {
	thsop: ConditionOperator;
	thsval: string | number | boolean;
	// _gui_purpose: 'condition';
	hold_off?: number | string;
	_gui_device_id?: string;
} & GUI_PURPOSE_COND;

type GUI_PURPOSE_COND = {
	_gui_purpose: 'condition';
};
/**
 * On any change executed it
 * {
 *  'operator_as_key': [
 *      { rr: 'dev:decimalId:condByCh'},
 *      desiredValue
 *  ],
 *  _gui_purpose: 'condition' //const
 * }
 */
export type DeviceAnyChangeTrigger = {
	eq?: [DeviceTrigger, string | number | boolean];
	lt?: [DeviceTrigger, string | number | boolean];
	gt?: [DeviceTrigger, string | number | boolean];
	_gui_purpose: string;
	_gui_device_id?: string; //optional: ovveride the gui device id
};

type TRIGGER_ON = {
	rrtrig: { rr: 'dt:t' };
};
/**
 * Repeatedly: (extends on any)
 * * {
 *  'operator_as_key': [
 *      { rr: 'dev:decimalId:condByCh'},
 *      desiredValue
 *  ],
 *  _gui_purpose: 'condition' //const,
 * rrtrig: { rr: 'dt:t'}
 * }
 */

export type DeviceRepeatTrigger = DeviceAnyChangeTrigger & TRIGGER_ON;

export type DeviceConditionR = {
	/** Polled device read. BLE report-only props (e.g. moisture_alarm) use `rr` instead. */
	r?: string;
	rr?: string;
	transform?: SceneConditionTransform;
};

/**
 *
 * Condition may be in this way:
 * {
 * r: `dev:decimalId:condByCh`,
 * cmpop: operator,
 * cmpval: desiredVal,
 * }
 * or
 * {
 *  'operator_as_key': [
 *      { r: 'dev:decimalId:condByCh'},
 *      desiredValue
 *  ],
 *  _gui_purpose: 'condition' //const,
 * }
 */

export type DeviceConditionOperatorAsKey = {
	eq?: [DeviceConditionR, string | number];
	lt?: [DeviceConditionR, string | number];
	gt?: [DeviceConditionR, string | number];
	_gui_device_id?: string; //optional: ovveride the gui device id
} & GUI_PURPOSE_COND;

export type DeviceConditionOperatorInR = DeviceConditionR & {
	cmpop: ConditionOperator;
	cmpval: string | number | boolean;
	_gui_device_id?: string; //optional: ovveride the gui device id for the condition
};

export type SceneAsTrigger = {
	rr: string;
	cmpop: 'eq';
	cmpval: 'true';
};

type EnergyTrigger = {
	rr: ISceneTrigger;
};

export type MathCondition = {
	any; //Math script (extended iots format) from the transpiler
	_gui_type: 'math_based';
	_gui_purpose: 'condition';
	_gui_math_expression: string; //Math expression
	_gui_math_params: MathParameter[]; //A, B, C, etc. parameters
};

export type ManualExecutionCondition = {
	_gui_type: 'manual_execution';
	_gui_purpose: 'condition';
};
export type ManualExecutionConditionUI = ManualExecutionCondition;

/** Energy Exchange
 * {
	"lt": [
		{
			"rr": "ppp:ibex:0" --->>> ISceneTrigger (EnergyExchange specific)
		},
		180
	],
	"_gui_purpose": "condition",
	"rrtrig": {
		"rr": "dt:t"
	}
}
 */
export type EnergyExchangeTrigger = {
	eq?: [EnergyTrigger, string | number];
	lt?: [EnergyTrigger, string | number];
	gt?: [EnergyTrigger, string | number];
	_gui_purpose: string;
} & TRIGGER_ON;

// math functions as conditions
// {gt:[{sum:[{r:...},{r:...}]},200]}

/** Type safe functions */
export function is_device_cond_op_in_r(o: any): o is DeviceConditionOperatorInR {
	if (typeof o !== 'object') return false;
	const ref = typeof o.r === 'string' ? o.r : typeof o.rr === 'string' ? o.rr : '';
	if (
		ref.startsWith('dev:') &&
		['eq', 'lt', 'gt'].includes(o.cmpop) &&
		typeof o.cmpval !== 'undefined'
	) {
		return true;
	}
	return false;
}

export function is_device_cond_op_as_key(o: any): o is DeviceConditionOperatorAsKey {
	if (
		typeof o === 'object' &&
		Object.keys(o).find((el) => el === 'eq' || el === 'lt' || el === 'mt') &&
		Object.values(o).find((el) => typeof (el as any).r !== 'undefined')
	) {
		return true;
	}
}

/** Expression-format device condition: `{ eq: [{ rr: "dev:…" }, value] }` (CBL / flow editor). */
export function is_device_expr_cond_with_ref(o: any): boolean {
	if (!o || typeof o !== 'object') return false;
	const op = ['eq', 'lt', 'gt', 'mt', 'gte', 'lte', 'neq'].find((k) => k in o);
	if (!op) return false;
	const operands = (o as Record<string, unknown>)[op];
	if (!Array.isArray(operands)) return false;
	return operands.some(
		(el: unknown) =>
			el != null &&
			typeof el === 'object' &&
			(typeof (el as { rr?: string }).rr === 'string' ||
				typeof (el as { r?: string }).r === 'string'),
	);
}

export function is_device_once_trigger(o: any): o is DeviceOnceTrigger {
	return (
		typeof o === 'object' &&
		typeof o?.rr === 'string' &&
		o?.rr?.startsWith('dev:') &&
		typeof o?.thsop === 'string' &&
		['string', 'number', 'boolean'].includes(typeof o.thsval) &&
		o._gui_purpose === 'condition'
	);
}

export function is_device_on_any_change_trigger(o: any): o is DeviceAnyChangeTrigger {
	return (
		typeof o === 'object' &&
		(typeof o.eq !== 'undefined' || typeof o.lt !== 'undefined' || typeof o.gt !== 'undefined') &&
		o._gui_purpose === 'condition'
	);
}

export function is_repeat_device_trigger(o: any): o is DeviceRepeatTrigger {
	return (
		typeof o === 'object' &&
		(typeof o.eq !== 'undefined' || typeof o.lt !== 'undefined' || typeof o.gt !== 'undefined') &&
		o._gui_purpose === 'condition' &&
		typeof o.rrtrig === 'object'
	);
}

export function is_scene_as_trigger(o: any): o is SceneAsTrigger {
	return (
		typeof o === 'object' &&
		typeof o?.rr === 'string' &&
		o?.rr?.split(':')[0] == 'nt' &&
		o.cmpop === 'eq' &&
		o.cmpval === 'true'
	);
}

export function is_sunrise_sunset_trigger(o: any): o is SunriseSunsetTrigger {
	return typeof o === 'object' && typeof o?.rr == 'string' && o?.rr?.match(/sun:/);
}

export function is_energy_exchange_trigger(o: any): o is EnergyExchangeTrigger {
	return (
		typeof o === 'object' &&
		(typeof o.eq !== 'undefined' || typeof o.lt !== 'undefined' || typeof o.gt !== 'undefined') &&
		typeof (Object.values(o)?.[0]?.[0] as any)?.rr === 'string' &&
		(Object.values(o)?.[0]?.[0] as any)?.rr.startsWith('ppp:') &&
		o._gui_purpose === 'condition' &&
		typeof o.rrtrig === 'object'
	);
}

export function is_weather_one_time_trigger(o: any): o is WeatherOnceTrigger {
	return (
		typeof o === 'object' &&
		o._gui_purpose === 'condition' &&
		['lt', 'gt', 'eq'].includes(o.thsop) &&
		typeof o?.rr === 'string' &&
		o?.rr?.startsWith('owm:')
	);
}

export function is_weather_on_any_change_trigger(o: any): o is WeatherTriggerOnAnyChange {
	return (
		typeof o === 'object' &&
		(typeof o.eq !== 'undefined' || typeof o.lt !== 'undefined' || typeof o.gt !== 'undefined') &&
		typeof (Object.values(o)?.[0]?.[0] as any)?.rr === 'string' &&
		(Object.values(o)?.[0]?.[0] as any)?.rr?.startsWith('owm:')
	);
}

/** Checks iots script for Math Condition */
export function is_math_condition(o: any): o is MathCondition {
	return (
		typeof o === 'object' &&
		o._gui_type === 'math_based' &&
		typeof o.math_expression === 'string' &&
		typeof o.math_params !== 'undefined'
	);
}

export function is_manual_execution_condition(o: any): o is ManualExecutionCondition {
	return (
		typeof o === 'object' && o._gui_type === 'manual_execution' && typeof o._gui_purpose === 'string'
	);
}

/** end type safe functions */
